/**
 * WaveControlRoom — the campaign-detail "flight deck" for a live Wave.
 *
 *   ▸ Horizontal 10-stage stepper (numbered dots + connecting line)
 *   ▸ Single active Stage Inspector panel (only the selected stage renders)
 *   ▸ Creator Propensity Table (live KPI-hit probability)
 *   ▸ Right rail Flight Deck — top 3 AI suggestions + collapsed levers list
 *
 * Routes: `/campaigns/:id`, `/waves/:id/live`, `/waves/:id/control`
 *
 * Design goal: zero clutter. Two columns. One stepper. One inspector.
 * Everything else hides behind progressive disclosure.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import { MetricExplainerIcon } from '../components/metrics/MetricWithExplainer';
import {
  ArrowLeft, Sparkles, Loader2, Pause, MessageCircle, Repeat2, Download, Share2, Check, BookmarkPlus, Copy, Grid3x3,
  Plus, Camera, Shuffle, Flag, Pin, Clock3, ArrowRightLeft,
  CircleDot, ChevronDown, ChevronUp, ChevronRight, AlertTriangle, ClipboardList,
} from 'lucide-react';
import CreatorAvatar, { CreatorAvatarStack } from '../components/creators/CreatorAvatar';
import ActionIntentModal, { shouldConfirm } from '../components/creators/ActionIntentModal';
import FlightDeck from '../components/campaigns/FlightDeck';
import CreatorProfileDrawer from '../components/creators/CreatorProfileDrawer';
import CohortIntelligencePanel from '../components/wave-control/CohortIntelligencePanel';
import CampaignWrapReport from '../components/wave-control/CampaignWrapReport';
import AccordionShell from '../components/wave-control/AccordionShell';
import WaveHeroV2 from '../components/wave-control/WaveHeroV2';
import DrivingTeamsSection from '../components/wave-control/DrivingTeamsSection';
import BriefSnapshotCard from '../components/wave-control/BriefSnapshotCard';

const API = process.env.REACT_APP_BACKEND_URL;

// ═════════════ stage accent colors ═════════════
const STAGE_COLORS = {
  velocity_band: '#00D4FF', unique_hook: '#FF4D94', patient_zero: '#FF4D94',
  burst_build: '#00D4FF', role_assignment: '#00D4FF', angle_assignment: '#FF4D94',
  cta_flagging: '#4ade80', post_stagger: '#00D4FF', velocity_curve: '#FF4D94',
  live_plan: '#4ade80',
};

// KPI-hit band → progress dot color. Flat lookup beats nested ternaries.
const BAND_COLORS = { high: '#4ade80', medium: '#facc15', low: '#FF6B6B' };

// Stage-stepper dot palette for each phase state (active / past / future).
function stepperDotPalette(isActive, isPast, color) {
  if (isActive) return { bg: color,          fg: '#0a0a0a', border: 'none' };
  if (isPast)   return { bg: `${color}40`,   fg: color,     border: `1px solid ${color}` };
  return             { bg: '#1a1a1a',        fg: '#666',    border: '1px solid #1f1f1f' };
}

// ═════════════ PAGE ═════════════
export default function WaveControlRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Continuity hint from the briefing's Act 4 handoff. When the brand lands
  // here straight from ignition, BriefingCanvas passes
  // { state: { fromIgnition, pzName, prePulse, waveName, creatorCount,
  //            projectedPulseTarget, compoundingLabel } } via react-router's
  // navigate(). We use these to:
  //   • show a soft "Wave ignited" pill at top for 4s (continuity)
  //   • seed the Pulse meter with the pre-polled value (non-zero on arrival)
  //   • render the actual wave name + creator count INSTANTLY so the brand
  //     never sees "Untitled Wave · 0 creators · all zeros" as a flash
  //     state. Skeleton render uses the seed values until the live /plan
  //     and /live-state APIs resolve. Every seed value is factual — no
  //     speculation introduced. Safe-surface contract preserved.
  const fromIgnition = location.state?.fromIgnition === true;
  const ignitionPzName = location.state?.pzName || null;
  const ignitionPrePulse = location.state?.prePulse ?? null;
  const seedWaveName = location.state?.waveName || null;
  const seedCreatorCount = location.state?.creatorCount ?? null;
  const seedProjectedPulseTarget = location.state?.projectedPulseTarget ?? null;
  const seedCompoundingLabel = location.state?.compoundingLabel || null;
  const [ignitionPillVisible, setIgnitionPillVisible] = useState(fromIgnition);
  useEffect(() => {
    if (!fromIgnition) return undefined;
    const t = setTimeout(() => setIgnitionPillVisible(false), 4200);
    return () => clearTimeout(t);
  }, [fromIgnition]);

  const [plan, setPlan] = useState(null);
  const [liveState, setLiveState] = useState(null);
  const [scores, setScores] = useState(null);
  const [pulseData, setPulseData] = useState(
    // Seed with the pre-polled value so the Pulse meter shows a non-zero
    // number on arrival when coming from ignition. Replaced by the real
    // /pulse response within ~500ms by the load() useEffect below.
    ignitionPrePulse != null
      ? { pulse_score: ignitionPrePulse, composites: {} }
      : null
  );
  const [leverForecasts, setLeverForecasts] = useState([]);
  const [cmdData, setCmdData] = useState(null);
  const [actionsLog, setActionsLog] = useState([]);
  const [activeStage, setActiveStage] = useState('velocity_band');
  const [toast, setToast] = useState(null);
  const [err, setErr] = useState(null);
  // Tracks whether the FIRST load() has completed. Until it does, sections
  // render skeletons. After it does, sections render real data even if a
  // specific field is empty (so we don't flash skeletons during 30s polls).
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { path } when intent modal open
  // iter 53: shareable public-link state
  const [shareUrl, setShareUrl] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);

  // iter 80: which non-hero accordion is currently expanded (single-open
  // behavior). The hero (#1) is always expanded; null = all others closed.
  const [openAccordion, setOpenAccordion] = useState(null);
  const toggleAccordion = (id) =>
    setOpenAccordion(curr => (curr === id ? null : id));

  // Wave 3 prep — cross-product creator profile drawer.
  // Today: opens with local DSP data only. Once SSP ships /embed/creator,
  // the drawer renders an iframe of their detail view below the summary.
  const [creatorDrawer, setCreatorDrawer] = useState(null); // {creator, context} when open

  const copyShareLink = async () => {
    setShareBusy(true);
    try {
      let absoluteUrl = shareUrl;
      if (!absoluteUrl) {
        const r = await axios.post(`${API}/api/brand/waves/${id}/public-share`, {}, { withCredentials: true });
        absoluteUrl = `${window.location.origin}${r.data.share_url}`;
        setShareUrl(absoluteUrl);
      }
      await navigator.clipboard.writeText(absoluteUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2400);
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Could not create share link.' });
      setTimeout(() => setToast(null), 3200);
    } finally {
      setShareBusy(false);
    }
  };

  // iter 56: save-as-template + clone-wave lifecycle
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);
  const [cloneBusy, setCloneBusy] = useState(false);

  const saveAsTemplate = async () => {
    const name = window.prompt('Name this template (e.g. "Q4 Hero Launch Pattern")');
    if (!name || !name.trim()) return;
    setTemplateBusy(true);
    try {
      await axios.post(`${API}/api/brand/waves/${id}/save-as-template`,
        { name: name.trim() },
        { withCredentials: true });
      setTemplateSaved(true);
      setToast({ type: 'ok', msg: `Saved "${name.trim()}" as a reusable template.` });
      setTimeout(() => { setTemplateSaved(false); setToast(null); }, 2800);
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Could not save template.' });
      setTimeout(() => setToast(null), 3200);
    } finally {
      setTemplateBusy(false);
    }
  };

  const cloneWave = async () => {
    // Fast path: save an inline template, clone it, nav to the new draft.
    setCloneBusy(true);
    try {
      const tpl = await axios.post(`${API}/api/brand/waves/${id}/save-as-template`,
        { name: `Clone · ${new Date().toLocaleDateString()}` },
        { withCredentials: true });
      const clone = await axios.post(`${API}/api/brand/wave-templates/${tpl.data.id}/clone`,
        {},
        { withCredentials: true });
      if (clone.data?.id) {
        navigate(`/waves/${clone.data.id}/control`);
      } else {
        throw new Error('No wave id returned from clone');
      }
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Could not clone wave.' });
      setTimeout(() => setToast(null), 3200);
    } finally {
      setCloneBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [p, l, s, a, pulse, fc, cmd] = await Promise.all([
        axios.get(`${API}/api/brand/waves/${id}/orchestration-plan`, { withCredentials: true }),
        axios.get(`${API}/api/brand/waves/${id}/live-state`, { withCredentials: true }),
        axios.get(`${API}/api/brand/waves/${id}/propensity-scores`, { withCredentials: true }),
        axios.get(`${API}/api/brand/waves/${id}/actions`, { withCredentials: true }),
        axios.get(`${API}/api/brand/waves/${id}/pulse`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/api/brand/waves/${id}/lever-forecast`, { withCredentials: true }).catch(() => ({ data: { forecasts: [] } })),
        axios.get(`${API}/api/brand/waves/${id}/cohort-manifold`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setPlan(p.data); setLiveState(l.data); setScores(s.data);
      setActionsLog(a.data?.actions || []);
      // Don't clobber the ignition pre-pulse seed with null if the /pulse
      // endpoint returns nothing — keep whatever we already have.
      if (pulse.data) setPulseData(pulse.data);
      setLeverForecasts(fc.data?.forecasts || []);
      setCmdData(cmd?.data || null);
      setFirstLoadDone(true);
    } catch (e) {
      // Soft failure — surface a toast but keep the skeleton chrome visible.
      // The brand should never see a black screen after ignition, even if the
      // backend is briefly unreachable. Hard error blocks were the cause of
      // the iter-88 P0 handoff failure.
      const msg = e?.response?.data?.detail || 'Could not load wave.';
      setErr(msg);
      setToast({ type: 'err', msg });
      setTimeout(() => setToast(null), 4000);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const runAction = async (path, body = { params: {} }) => {
    try {
      const r = await axios.post(`${API}/api/brand/waves/${id}/actions/${path}`,
        body, { withCredentials: true });
      const nc = r.data.notified_count;
      const msg = `${r.data.label || r.data.action_type.replace(/_/g, ' ')} queued · ${r.data.preview_cost_usd ? `$${Number(r.data.preview_cost_usd).toLocaleString()}` : 'free'}${nc ? ` · ${nc} creator${nc === 1 ? '' : 's'} notified` : ''}`;
      setToast({ type: 'ok', msg });
      setTimeout(() => setToast(null), 3800);
      load();
    } catch (e) {
      setToast({ type: 'err', msg: e?.response?.data?.detail || 'Action failed' });
      setTimeout(() => setToast(null), 4000);
    }
  };

  // Entry point every Flight Deck button calls. Gates through the Intent Modal
  // for any lever that costs money or mutates state; fires immediately for
  // zero-cost broadcast actions (hero-mirror, reply-storm).
  //
  // `cmdRecommendationId` is the telemetry join key — when the brand fires
  // the lever from a CMD recommendation card, the originating recommendation's
  // stable ID is threaded through here and stamped onto the wave_action doc
  // so calibration can join wave_actions → originating CMD recommendation
  // later. Pure observability — null on heuristic / manual fires.
  const triggerAction = (path, cmdRecommendationId = null) => {
    const body = { params: {} };
    if (cmdRecommendationId) body.cmd_recommendation_id = cmdRecommendationId;
    if (shouldConfirm(path)) setPendingAction({ path, body });
    else runAction(path, body);
  };

  // ─────────────────────────────────────────────────────────────
  //  Skeleton-first render.
  //  The page chrome (back link, action buttons, header, accordion
  //  shells) always renders immediately. Sections fill in as their
  //  APIs resolve. We NEVER block on a full-page spinner — that was
  //  the iter-88 black-screen handoff failure.
  //
  //  Hard error still rendered as a soft toast (set above in load()),
  //  not as a full-page block, so the cinematic baton stays
  //  uninterrupted even on a transient backend hiccup.
  // ─────────────────────────────────────────────────────────────
  const w = liveState?.wave || {};
  const t = liveState?.totals || {};
  const kpis = liveState?.kpi_progress || [];
  const creators = scores?.creators || [];

  return (
    <DashboardLayout>
      <div
        className="p-5 lg:p-8 max-w-[1500px] mx-auto"
        data-testid="wave-control-room"
        data-first-load-done={firstLoadDone ? 'true' : 'false'}
        data-from-ignition={fromIgnition ? 'true' : 'false'}
        style={{
          // Entrance animation when coming from ignition — the canvas
          // fades in from a slight pink-tinted scale so the brand reads
          // it as the briefing canvas resolving into the live dashboard,
          // not a hard cut.
          animation: fromIgnition
            ? 'controlRoomIgniteEntrance 700ms cubic-bezier(0.4, 0, 0.2, 1) both'
            : 'none',
        }}
      >

        {/* Ignition continuity pill — visible for 4.2s when arriving
            fresh from the briefing's Act 4. Sits at the top of the
            page, above the back link, with a soft pink glow that fades
            into the rest of the chrome. */}
        {ignitionPillVisible && (
          <div
            data-testid="ignition-continuity-pill"
            className="mb-4 flex justify-center"
            style={{ animation: 'ignitionPillFade 4200ms ease-out both' }}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: 'rgba(255,77,148,0.10)',
                border:     '1px solid rgba(255,77,148,0.40)',
                color:      '#FF4D94',
                boxShadow:  '0 0 24px rgba(255,77,148,0.25)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D94] animate-pulse" />
              <span className="text-[11px] font-black uppercase tracking-widest">
                Wave ignited{ignitionPzName ? ` · ${ignitionPzName} firing` : ''}
              </span>
            </div>
          </div>
        )}

        {/* Back */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link to="/campaigns" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#00D4FF] transition-colors" data-testid="back-to-campaigns">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Campaigns
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to={`/waves/${id}/formation`}
              data-testid="view-formation-btn"
              title="See cohort in play-formation view"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white hover:brightness-110"
              style={{ background: 'linear-gradient(90deg, #FF4D94 0%, #00D4FF 100%)' }}
            >
              <Grid3x3 className="h-3 w-3" /> Formation
            </Link>
            <button
              type="button"
              onClick={saveAsTemplate}
              disabled={templateBusy}
              data-testid="save-as-template-btn"
              title="Save this wave's plan as a reusable template"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border hover:brightness-110 disabled:opacity-60"
              style={{ color: templateSaved ? '#4ade80' : '#fff', background: 'transparent', borderColor: templateSaved ? '#4ade80' : '#1f1f1f' }}
            >
              {templateBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : templateSaved ? <Check className="h-3 w-3" /> : <BookmarkPlus className="h-3 w-3" />}
              {templateSaved ? 'Saved' : 'Save template'}
            </button>
            <button
              type="button"
              onClick={cloneWave}
              disabled={cloneBusy}
              data-testid="clone-wave-btn"
              title="Create a new draft wave from this wave's plan"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border hover:brightness-110 disabled:opacity-60"
              style={{ color: '#fff', background: 'transparent', borderColor: '#1f1f1f' }}
            >
              {cloneBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
              Clone wave
            </button>
            <button
              type="button"
              onClick={copyShareLink}
              disabled={shareBusy}
              data-testid="share-wave-link-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border hover:brightness-110 disabled:opacity-60"
              style={{ color: shareCopied ? '#4ade80' : '#fff', background: 'transparent', borderColor: shareCopied ? '#4ade80' : '#1f1f1f' }}
            >
              {shareBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : shareCopied ? <Check className="h-3 w-3" /> : <Share2 className="h-3 w-3" />}
              {shareCopied ? 'Link copied' : 'Share link'}
            </button>
            <Link
              to={`/waves/${id}/blueprint`}
              data-testid="view-blueprint-link"
              title="See the personalized 10-stage Wave Blueprint"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border hover:brightness-110"
              style={{ color: '#FF4D94', background: 'transparent', borderColor: '#FF4D94' }}
            >
              <ClipboardList className="h-3 w-3" /> Wave blueprint
            </Link>
            <Link
              to={`/waves/${id}/reports`}
              data-testid="view-reports-link"
              title="Open the CMD-powered Reporting Suite for this wave"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border hover:brightness-110"
              style={{ color: '#A78BFA', background: 'transparent', borderColor: '#A78BFA' }}
            >
              <Grid3x3 className="h-3 w-3" /> Reports
            </Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/brand/waves/${id}/export.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="export-wave-pdf-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider hover:brightness-110"
              style={{ color: '#00D4FF', background: 'transparent', border: '1px solid rgba(0,212,255,0.4)' }}
            >
              <Download className="h-3 w-3" /> Performance PDF
            </a>
          </div>
        </div>

        {/* Hero */}
        <header className="mt-3 flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">Wave Control Room</p>
              <StatusBadge status={w.display_status || w.status} />
              {/* iter 79: Auto-Scheduler pill — reframes density as a platform
                  guarantee rather than something the brand worries about. */}
              {['live','sent','active'].includes(w.display_status || w.status) && (
                <span
                  data-testid="auto-scheduler-pill"
                  className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(74,222,128,0.10)',
                    color: '#4ade80',
                    border: '1px solid rgba(74,222,128,0.30)',
                  }}
                  title="Auto-Scheduler holds the burst window for every accepted creator. Brand doesn't manage talent timing."
                >
                  <span className="w-1 h-1 rounded-full bg-[#4ade80] animate-pulse" />
                  Auto-Scheduler · Active · Locked {t.fired_posts || 0}/{t.total_posts || 0}
                </span>
              )}
            </div>
            <h1
              className="text-3xl font-black text-white tracking-tight"
              data-testid="wave-name"
            >
              {plan?.wave_name || seedWaveName || 'Untitled Wave'}
            </h1>
            <p className="text-xs text-gray-500 mt-1">
              {(w.platforms || []).filter(Boolean).join(' · ') || 'all platforms'}
              {' · '}
              {/* Creator count — once live data is in we use w.creator_count
                  or creators.length. Before that, seedCreatorCount (passed
                  from the briefing via navigate state) renders instantly so
                  the brand never sees "0 creators" on arrival. */}
              {w.creator_count || creators.length || seedCreatorCount || 0} creators
              {w.unique_hook ? <> · <span className="text-[#FF4D94] font-mono">{w.unique_hook}</span></> : null}
            </p>
          </div>
          {/* The 3 secondary KPI tiles (Reach / Engagement / ROAS) are now
              rendered INSIDE the Pulse Hero card below — see iter 60 v2
              feedback ("the empty box with the wave pulse doesn't look
              good"). Keeping the header lean. */}
        </header>

        {/* Horizontal Stage Stepper - REMOVED. The 10-stage Blueprint is now its
            own dedicated page at /waves/:id/blueprint. The Control Room focuses
            exclusively on what's happening live. */}

        {/* iter 80 — Single column · 5-accordion layout
            ────────────────────────────────────────────
            #1 Campaign Performance  — always expanded (hero)
            #2 Driving Teams         — ranked list w/ click-to-drill
            #3 In-Flight Optimization — FlightDeck content + log
            #4 Top Creators          — CreatorPropensityTable
            #5 Campaign Wrap Report  — auto-enabled at ≥80% complete (renamed from Cohort Post-Mortem in iter 91)
            The right-rail Flight Deck is gone — its contents live in #3. */}
        <div className="space-y-3 mt-6">
          {/* ── #1 · Campaign Performance · ALWAYS OPEN ── */}
          <AccordionShell
            id="campaign-performance"
            index={1}
            title="Campaign Performance"
            alwaysOpen
            chip={
              pulseData?.metrics_state === 'awaiting_results' ? (
                <>Awaiting results · real platform metrics fetch ships post-Phase-0</>
              ) : (
                <>
                  Pulse <b style={{ color: '#fff' }}>{Math.round(pulseData?.pulse_score ?? 0)}</b>
                  {' '}· all composites feeding this score
                </>
              )
            }
          >
            {pulseData?.metrics_state === 'simulated_illustrative' && (
              <div className="mb-2 flex">
                <span data-testid="e011-simulated-chip"
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest"
                      style={{ background: 'rgba(255,184,77,0.10)', border: '1px solid rgba(255,184,77,0.35)', color: '#FFB84D' }}>
                  Simulated — illustrative
                </span>
              </div>
            )}
            <WaveHeroV2
              waveId={id}
              pulseData={pulseData}
              totals={liveState?.totals || {}}
              kpiBenchmarks={liveState?.kpi_benchmarks || []}
              kpiProgress={liveState?.kpi_progress || []}
              cmdInsights={cmdData?.composite_insights || {}}
              onBoostBudget={() => triggerAction('spawn-burst')}
              ghostTarget={seedProjectedPulseTarget}
              warmingUp={fromIgnition && !firstLoadDone}
            />
          </AccordionShell>

          {/* ── Brief Snapshot · read-only mirror of what creators received ──
              Lives between the hero and the next accordion so a brand rep
              can eyeball the brief at a glance. Auto-collapses; expands on
              click. Renders the `frozen_at_send.brief` (audit-trail truth)
              when the wave has been sent. */}
          <BriefSnapshotCard waveId={id} />

          {/* ── #2 · Driving Teams ── */}
          {cmdData?.enabled && (cmdData.patches?.length || 0) > 0 && (
            <AccordionShell
              id="driving-teams"
              index={2}
              title="Driving Teams"
              open={openAccordion === 'driving-teams'}
              onToggle={() => toggleAccordion('driving-teams')}
              chip={
                <>
                  {(cmdData.patches || []).length} teams · ranked by Pulse impact · click to unpack live performance
                </>
              }
            >
              <DrivingTeamsSection
                patches={cmdData.patches}
                creators={creators}
                compositeInsights={(() => {
                  // Enrich each composite insight envelope with its score
                  // so the DrivingTeamsSection can scale leader/lagger
                  // deltas by composite strength (75 → +7, 30 → +3).
                  const base = cmdData.composite_insights || {};
                  const composites = pulseData?.composites || {};
                  return Object.fromEntries(Object.entries(base).map(([k, v]) => [
                    k,
                    { ...v, score: composites[k]?.score ?? v?.score ?? 50 },
                  ]));
                })()}
                liveAssignments={liveState?.assignments || []}
                onLeverFire={(path) => triggerAction(path)}
              />
            </AccordionShell>
          )}

          {/* ── #3 · In-Flight Optimization · FlightDeck + Optimization Log ── */}
          <AccordionShell
            id="in-flight-optimization"
            index={3}
            title="In-Flight Optimization"
            open={openAccordion === 'in-flight-optimization'}
            onToggle={() => toggleAccordion('in-flight-optimization')}
            chip={
              <>
                {(buildSuggestions(liveState, creators, cmdData) || []).length} recommendations
                {actionsLog?.length > 0 && (
                  <> · <span style={{ color: '#4ade80' }}>{actionsLog.length} lever{actionsLog.length === 1 ? '' : 's'} fired</span></>
                )}
              </>
            }
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <FlightDeck
                  onAction={triggerAction}
                  suggestions={buildSuggestions(liveState, creators, cmdData)}
                  waveStatus={w.display_status || w.status}
                />
              </div>
              <div>
                <OptimizationLog actions={actionsLog} waveId={id} />
              </div>
            </div>
          </AccordionShell>

          {/* ── #4 · Top Creators · Propensity Table ── */}
          <AccordionShell
            id="top-creators"
            index={4}
            title="Top Creators"
            open={openAccordion === 'top-creators'}
            onToggle={() => toggleAccordion('top-creators')}
            chip={
              <>
                {(creators || []).length} creators ranked by KPI-hit probability · click any to open profile
              </>
            }
          >
            <CreatorPropensityTable
              creators={creators}
              formula={scores?.formula}
              onAction={triggerAction}
            />
          </AccordionShell>

          {/* ── #5 · Campaign Wrap Report · unlocks at ≥80% complete ──
              iter 91: renamed from "Cohort Post-Mortem". Now a
              retrospective surface — no action buttons, no pulse deltas,
              no cost figures, no live-forecast badges. Max 3 observation
              rows, past-tense narrative + next-wave recommendation. The
              live in-flight levers that used to render here belong in
              accordion #3 (In-Flight Optimization) during a live wave. */}
          {(() => {
            const ds = (w.display_status || w.status || '').toLowerCase();
            const completed = ['complete','completed','past','closed','archived'].includes(ds);
            const totals = liveState?.totals || {};
            const firedPct = totals.total_posts > 0
              ? Math.round((totals.fired_posts || 0) / totals.total_posts * 100)
              : 0;
            const unlocked = completed || firedPct >= 80;
            return (
              <AccordionShell
                id="campaign-wrap-report"
                index={5}
                title="Campaign Wrap Report"
                disabled={!unlocked}
                open={openAccordion === 'campaign-wrap-report'}
                onToggle={() => toggleAccordion('campaign-wrap-report')}
                chip={
                  unlocked
                    ? <>Key learnings from this wave · view the full report in Insights &amp; Analytics.</>
                    : <>Available at 80% complete · currently {firedPct}% fired</>
                }
              >
                <CampaignWrapReport
                  pulseData={pulseData}
                  totals={liveState?.totals}
                  creators={creators}
                  waveId={id}
                />
              </AccordionShell>
            );
          })()}
        </div>
      </div>

      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg border text-xs font-semibold shadow-2xl z-50 ${toast.type === 'ok' ? 'bg-[#00D4FF]/15 border-[#00D4FF]/40 text-[#00D4FF]' : 'bg-red-500/15 border-red-500/40 text-red-400'}`}
             data-testid="action-toast">{toast.msg}</div>
      )}

      {pendingAction && (
        <ActionIntentModal
          path={pendingAction.path}
          creators={scores?.creators || []}
          onClose={() => setPendingAction(null)}
          onConfirm={(path, body) => {
            // Preserve the CMD recommendation join key across the intent
            // modal — the modal builds its own body but doesn't know about
            // telemetry plumbing.
            const merged = { ...(body || {}) };
            if (pendingAction.body?.cmd_recommendation_id) {
              merged.cmd_recommendation_id = pendingAction.body.cmd_recommendation_id;
            }
            setPendingAction(null);
            runAction(path, merged);
          }}
        />
      )}
      {/* Wave 3 prep · cross-product creator profile drawer. */}
      <CreatorProfileDrawer
        open={!!creatorDrawer}
        creator={creatorDrawer?.creator}
        context={creatorDrawer?.context}
        onClose={() => setCreatorDrawer(null)}
      />

      {/* Scoped keyframes — entrance from ignition handoff + continuity pill */}
      <style>{`
        @keyframes controlRoomIgniteEntrance {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1);   }
        }
        @keyframes ignitionPillFade {
          0%   { opacity: 0; transform: translateY(-4px); }
          12%  { opacity: 1; transform: translateY(0);    }
          80%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes skeletonShimmer {
          0%   { background-position: -400px 0; }
          100% { background-position: 400px 0;  }
        }
        .control-skel {
          background: linear-gradient(90deg, #141414 0%, #1a1a1a 50%, #141414 100%);
          background-size: 800px 100%;
          animation: skeletonShimmer 1400ms ease-in-out infinite;
          border-radius: 6px;
        }
      `}</style>
    </DashboardLayout>
  );
}

// ═════════════ StatusBadge ═════════════
function StatusBadge({ status }) {
  const map = {
    sent:      { label: 'LIVE',      bg: 'bg-green-500/15', color: 'text-green-400', pulse: true },
    live:      { label: 'LIVE',      bg: 'bg-green-500/15', color: 'text-green-400', pulse: true },
    active:    { label: 'LIVE',      bg: 'bg-green-500/15', color: 'text-green-400', pulse: true },
    paused:    { label: 'PAUSED',    bg: 'bg-yellow-500/15', color: 'text-yellow-400', pulse: false },
    completed: { label: 'COMPLETED', bg: 'bg-blue-500/15',  color: 'text-blue-400',  pulse: false },
  };
  const cfg = map[status] || { label: (status || '—').toUpperCase(), bg: 'bg-[#1f1f1f]', color: 'text-gray-400', pulse: false };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider ${cfg.bg} ${cfg.color}`} data-testid="wave-status-badge">
      {cfg.pulse && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />}
      {cfg.label}
    </span>
  );
}

// ═════════════ KpiTile (compact, one-line metric) ═════════════
function KpiTile({ label, current, target, color, metric, suffix = '' }) {
  const pct = target && current ? Math.min(100, Math.round((current / target) * 100)) : 0;
  return (
    <div className="rounded-xl px-4 py-2.5 min-w-[120px]" style={{ background: '#0F0F0F', border: `1px solid ${color}30` }}>
      <div className="flex items-center gap-1 mb-0.5">
        <p className="text-[9px] uppercase tracking-[0.15em] text-gray-500 font-bold">{label}</p>
        {metric && <MetricExplainerIcon metric={metric} size="xs" />}
      </div>
      <p className="text-lg font-black tabular-nums leading-tight" style={{ color }}>
        {typeof current === 'number' ? `${current.toLocaleString()}${suffix}` : '—'}
      </p>
      {target ? (
        <div className="mt-1 h-[3px] rounded-full bg-[#1f1f1f] overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, transition: 'width 600ms ease-out' }} />
        </div>
      ) : <div className="h-[3px]" />}
    </div>
  );
}

// ═════════════ StageStepper — horizontal timeline ═════════════
export function StageStepper({ plan, active, setActive, waveStatus }) {
  const activeIdx = plan.stages.findIndex(s => s.id === active);
  const isLive = (waveStatus || '').toLowerCase() === 'sent' || (waveStatus || '').toLowerCase() === 'live';
  return (
    <nav
      className="relative rounded-2xl bg-[#0F0F0F] border border-[#1f1f1f] px-5 py-5 overflow-x-auto"
      data-testid="stage-stepper"
      aria-label="10 stages of orchestration"
    >
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF4D94] font-bold">The Wave Blueprint · 10 Stages</p>
          <p className="text-[11px] text-gray-400 mt-1 max-w-2xl leading-relaxed">
            Stages 1–9 are <span className="text-white font-bold">pre-flight plan</span> — locked when you sent the wave.
            Stage 10 is <span className="text-white font-bold">live execution</span>, powered by the Adaptive Burst Engine below.
          </p>
        </div>
        <p className="text-[10px] text-gray-600 hidden sm:block">click any stage to inspect</p>
      </div>

      <div className="relative min-w-[860px]">
        {/* connecting line */}
        <div className="absolute top-4 left-4 right-4 h-[1px] bg-[#1f1f1f]" />

        <ol className="relative flex items-start justify-between">
          {plan.stages.map((s, i) => {
            const isActive = s.id === active;
            const isPast = i < activeIdx;
            const isLiveStage = i === plan.stages.length - 1; // stage 10 = live
            const isPreFlight = !isLiveStage;
            const color = STAGE_COLORS[s.id] || '#00D4FF';
            return (
              <li key={s.id} className="flex flex-col items-center flex-1 relative">
                <button
                  type="button"
                  onClick={() => setActive(s.id)}
                  data-testid={`stage-${s.id}`}
                  className="group flex flex-col items-center focus:outline-none"
                >
                  {(() => {
                    const p = stepperDotPalette(isActive, isPast, color);
                    const livePulse = isLiveStage && isLive && !isActive;
                    return (
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-black transition-all z-10 ${isActive ? 'scale-110' : 'group-hover:scale-105'} ${livePulse ? 'animate-pulse' : ''}`}
                        style={{
                          background: p.bg,
                          color: p.fg,
                          border: p.border,
                          boxShadow: isActive ? `0 0 16px ${color}60` : (livePulse ? `0 0 14px ${color}80` : 'none'),
                        }}
                      >
                        {s.num}
                      </span>
                    );
                  })()}
                  <span
                    className={`mt-2 text-[10px] font-bold leading-tight text-center max-w-[92px] transition-colors ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`}
                  >
                    {s.title}
                  </span>
                  <span
                    className={`mt-1 text-[8px] uppercase tracking-[0.15em] font-black ${
                      isLiveStage
                        ? (isLive ? 'text-[#4ade80]' : 'text-gray-600')
                        : 'text-gray-600'
                    }`}
                  >
                    {isPreFlight ? 'PRE-FLIGHT' : (isLive ? 'LIVE' : 'IDLE')}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

// ═════════════ StageInspector ═════════════
export function StageInspector({ stage, creators }) {
  if (!stage) return null;
  const color = STAGE_COLORS[stage.id] || '#00D4FF';
  return (
    <section
      className="rounded-2xl bg-[#0F0F0F] border p-6"
      style={{ borderColor: `${color}30` }}
      data-testid={`stage-inspector-${stage.id}`}
    >
      <header className="flex items-start gap-4 mb-5">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0" style={{ background: color, color: '#0a0a0a' }}>
          {stage.num}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 font-bold">Stage {stage.num} of 10</p>
          <h3 className="text-xl font-black text-white leading-tight mt-0.5">{stage.title}</h3>
          <p className="text-[12px] text-gray-400 leading-relaxed mt-1.5">{stage.why}</p>
        </div>
      </header>
      <StageContent stage={stage} color={color} creators={creators} />
    </section>
  );
}

function StageContent({ stage, color, creators = [] }) {
  const d = stage.data || {};

  if (stage.id === 'velocity_band') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(d.all_bands || {}).map(([name, cfg]) => {
          const active = name === d.band;
          return (
            <div key={name} className="rounded-xl p-3 border" style={{ background: active ? `${color}12` : '#0A0A0A', borderColor: active ? color : '#1f1f1f' }}>
              <p className="text-xs font-black mb-2" style={{ color: active ? color : '#fff' }}>{name}</p>
              <dl className="text-[10px] text-gray-400 space-y-1">
                <Row k="Bursts"   v={cfg.bursts} />
                <Row k="Creators" v={cfg.creators} />
                <Row k="Window"   v={cfg.window} />
                <Row k="Spacing"  v={cfg.spacing} />
              </dl>
            </div>
          );
        })}
      </div>
    );
  }

  if (stage.id === 'unique_hook') {
    return (
      <div className="flex items-center gap-4 p-5 rounded-xl bg-[#0A0A0A] border border-[#1f1f1f]">
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `${color}20` }}>
          <Flag className="h-5 w-5" style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-black font-mono tracking-tight" style={{ color }}>{d.hook || '—'}</p>
          <p className="text-[10px] text-gray-500 mt-1">tracked across {(d.platforms || []).join(', ') || 'all platforms'}</p>
        </div>
      </div>
    );
  }

  if (stage.id === 'patient_zero') {
    const pz = d.patient_zero;
    const pzFull = pz ? (creators.find(c => c.creator_handle === pz.handle) || {}) : {};
    return (
      <div className="space-y-3">
        <div className="rounded-xl p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-600 mb-1">Formula</p>
          <p className="text-[11px] text-[#00D4FF] font-mono">{d.formula}</p>
        </div>
        {pz ? (
          <div className="rounded-xl p-4 border flex items-center gap-4" style={{ background: `${color}10`, borderColor: `${color}40` }}>
            <CreatorAvatar handle={pz.handle} src={pzFull.creator_avatar} size={48} ring ringColor={color} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CircleDot className="h-4 w-4" style={{ color }} />
                <p className="text-sm font-black text-white truncate">{pz.name || pz.handle} · <span className="text-gray-400 font-normal">{pz.handle}</span></p>
              </div>
              <p className="text-[10px] text-gray-400 truncate mt-0.5">{pz.platform} · {(pz.followers || 0).toLocaleString()} followers · {pz.angle || '—'}</p>
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-gray-500">Patient Zero assignment pending.</p>
        )}
      </div>
    );
  }

  if (stage.id === 'burst_build') {
    return (
      <div className="space-y-3">
        {(d.bursts || []).map(b => (
          <div key={b.burst_number} className="rounded-xl p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-black text-white">Burst {b.burst_number}</p>
              <span className="text-[10px] text-gray-500">{b.count} creators</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(b.creators || []).map(c => {
                const full = creators.find(x => x.creator_handle === c.handle) || {};
                const isPZ = c.role === 'patient_zero';
                return (
                  <span key={c.handle} className="inline-flex items-center gap-1.5 pl-0.5 pr-2.5 py-0.5 rounded-full text-[10px] bg-[#121212] border border-[#1f1f1f] text-gray-300">
                    <CreatorAvatar handle={c.handle} src={full.creator_avatar} size={20} ring={isPZ} ringColor={isPZ ? '#FF4D94' : undefined} />
                    <span className="font-medium">{c.handle}</span>
                  </span>
                );
              })}
            </div>
          </div>
        ))}
        {!(d.bursts || []).length && <p className="text-[11px] text-gray-500">No bursts built yet.</p>}
      </div>
    );
  }

  if (stage.id === 'role_assignment' || stage.id === 'angle_assignment') {
    const key = stage.id === 'role_assignment' ? 'role' : 'angle';
    const entries = Object.entries(stage.id === 'role_assignment' ? (d.roles || {}) : (d.angles || {}));
    if (!entries.length) return <p className="text-[11px] text-gray-500">Not yet assigned.</p>;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {entries.map(([k, v]) => {
          const pct = d.pct?.[k] || 0;
          const members = creators
            .filter(c => (c[key] || '').toLowerCase() === k.toLowerCase())
            .map(c => ({ handle: c.creator_handle, avatar: c.creator_avatar }));
          return (
            <div key={k} className="rounded-xl p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">{k.replace(/_/g, ' ')}</p>
                <CreatorAvatarStack creators={members} size={22} max={5} />
              </div>
              <p className="text-xl font-black text-white mt-1">{v} <span className="text-[10px] font-normal text-gray-500">({pct}%)</span></p>
              <div className="mt-1.5 h-[3px] rounded-full bg-[#1f1f1f]"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></div>
            </div>
          );
        })}
      </div>
    );
  }

  if (stage.id === 'cta_flagging') {
    return (
      <div className="rounded-xl p-4 bg-[#0A0A0A] border border-[#1f1f1f]">
        <div className="flex items-baseline justify-between mb-2">
          <p className="text-3xl font-black text-white tabular-nums">{d.pct || 0}<span className="text-lg text-gray-500">%</span></p>
          <p className="text-[10px] text-gray-500">target: {d.target_pct}%</p>
        </div>
        <p className="text-[11px] text-gray-400">{d.cta_required || 0} of {d.total || 0} creators flagged with CTA-required briefs.</p>
      </div>
    );
  }

  if (stage.id === 'post_stagger') {
    const times = (d.scheduled_times || []).map(s => new Date(s)).filter(t => !isNaN(t));
    if (!times.length) return <p className="text-[11px] text-gray-500">No scheduled times yet.</p>;
    const min = Math.min(...times.map(t => t.getTime()));
    const max = Math.max(...times.map(t => t.getTime()));
    const hours = Math.max(1, Math.round((max - min) / 3600000));
    return (
      <div className="rounded-xl p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Scheduled firings across {hours}h window</p>
        <div className="relative h-10 bg-[#121212] rounded-lg overflow-hidden">
          {times.map(tm => {
            const ts = tm.getTime();
            const pct = max === min ? 50 : ((ts - min) / (max - min)) * 100;
            return <span key={ts} className="absolute top-1 bottom-1 w-[2px] opacity-70" style={{ left: `${pct}%`, background: color }} />;
          })}
        </div>
      </div>
    );
  }

  if (stage.id === 'velocity_curve') {
    return (
      <div className="rounded-xl p-4 bg-[#0A0A0A] border border-[#1f1f1f]">
        <p className="text-[11px] text-gray-400">The live wave's actual velocity curve is compared against the predicted shape every minute. Deviation &gt; 20% triggers a reaccelerate recommendation in the Flight Deck.</p>
      </div>
    );
  }

  if (stage.id === 'live_plan') {
    return (
      <div className="rounded-xl p-4 bg-[#0A0A0A] border border-[#1f1f1f] grid grid-cols-1 sm:grid-cols-3 gap-4 text-[11px]">
        <div><p className="text-gray-500">Status</p><p className="text-white font-bold mt-0.5">{(d.status || '—').toString().toUpperCase()}</p></div>
        <div><p className="text-gray-500">Sent at</p><p className="text-white font-bold mt-0.5">{d.sent_at ? new Date(d.sent_at).toLocaleString() : '—'}</p></div>
        <div><p className="text-gray-500">Reaccelerate</p><p className="text-white font-bold mt-0.5">{d.reacceleration_decision || 'on_schedule'}</p></div>
      </div>
    );
  }

  return null;
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-gray-600">{k}</dt>
      <dd className="text-white font-medium">{v}</dd>
    </div>
  );
}

// ═════════════ CreatorPropensityTable ═════════════
// `formula` accepted for backward-compat but no longer rendered (was leaking
// proprietary signal weights). Per-row signal weights still surface inside
// the expanded breakdown (only after a click), which is appropriate for
// brand reps who own the wave but invisible at the surface.
function CreatorPropensityTable({ creators, formula: _formula, onAction }) {
  const [expanded, setExpanded] = useState(null);
  const total = creators.length;
  const liveCount = creators.filter(c => c.status === 'live' || c.status === 'posted').length;
  const highBand = creators.filter(c => (c.kpi_hit_probability?.band || '') === 'high').length;
  return (
    <section className="rounded-2xl bg-[#0F0F0F] border border-[#1f1f1f] overflow-hidden" data-testid="creator-propensity-table">
      <header className="px-5 py-4 border-b border-[#1f1f1f] flex items-center justify-between flex-wrap gap-3"
              style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.06), rgba(255,77,148,0.04))' }}>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#00D4FF] font-bold flex items-center gap-1.5">
            Live Creator Propensity <MetricExplainerIcon metric="burst_power" size="xs" />
          </p>
          <p className="text-[12px] text-gray-300 mt-1 leading-relaxed">
            Live KPI-hit probability per creator. Tap any row to see the signal breakdown.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#4ade80]/30 text-[#4ade80] bg-[#4ade80]/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80] animate-pulse" />
            {liveCount}/{total} firing
          </span>
          {highBand > 0 && (
            <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#FF4D94]/30 text-[#FF4D94] bg-[#FF4D94]/10">
              {highBand} high-confidence
            </span>
          )}
        </div>
      </header>
      <div className="max-h-[520px] overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-[#0A0A0A] border-b border-[#1f1f1f] sticky top-0 z-10">
            <tr>
              {['Creator', 'Role', 'Burst', 'Propensity', 'KPI Hit', 'Status'].map(h => (
                <th key={h} className="px-4 py-2.5 text-[9px] uppercase tracking-[0.15em] text-gray-500 font-bold text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1f1f1f]">
            {creators.map(c => {
              const isOpen = expanded === c.assignment_id;
              const kpiPct = c.kpi_hit_probability?.overall || 0;
              const band = c.kpi_hit_probability?.band || 'low';
              const bandColor = BAND_COLORS[band] || BAND_COLORS.low;
              const propScore = c.propensity_score || 0;
              const propAngle = Math.min(360, Math.max(0, propScore * 3.6));
              const isLive = c.status === 'live' || c.status === 'posted';
              return (
                <React.Fragment key={c.assignment_id}>
                  <tr onClick={() => setExpanded(isOpen ? null : c.assignment_id)}
                      className="hover:bg-[#0A0A0A] cursor-pointer transition-colors group"
                      data-testid={`creator-${c.assignment_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <CreatorAvatar handle={c.creator_handle} src={c.creator_avatar} size={36}
                                       ring={isLive} ringColor="#4ade80" />
                        <div className="min-w-0">
                          <p className="text-white font-bold truncate group-hover:text-[#00D4FF] transition-colors">{c.creator_handle}</p>
                          <p className="text-[10px] text-gray-500">{c.platform} · {(c.followers || 0).toLocaleString()} followers</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-gray-300">
                        {(c.role || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[11px] font-black tabular-nums px-2 py-0.5 rounded-md bg-[#1f1f1f] text-gray-300">
                        #{c.burst_number}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {/* Conic-gradient propensity ring — visual at-a-glance */}
                      <div className="flex items-center gap-2">
                        <div className="relative w-7 h-7">
                          <div
                            className="absolute inset-0 rounded-full"
                            style={{ background: `conic-gradient(#00D4FF ${propAngle}deg, #1f1f1f ${propAngle}deg)` }}
                          />
                          <div className="absolute inset-[3px] rounded-full bg-[#0F0F0F] flex items-center justify-center">
                            <span className="text-[8px] font-black tabular-nums text-[#00D4FF]">{propScore}</span>
                          </div>
                        </div>
                        <span className="text-[10px] text-gray-500 font-bold">/100</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1 rounded-full bg-[#1f1f1f] overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500"
                               style={{ width: `${kpiPct}%`, background: bandColor, boxShadow: `0 0 6px ${bandColor}80` }} />
                        </div>
                        <span className="tabular-nums font-black text-[11px]" style={{ color: bandColor }}>{kpiPct}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
                        isLive
                          ? 'bg-[#4ade80]/15 text-[#4ade80] border border-[#4ade80]/30'
                          : 'bg-[#1f1f1f] text-gray-500'
                      }`}>
                        {isLive && <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80] animate-pulse" />}
                        {c.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-[#080808]">
                      <td colSpan={6} className="px-4 py-3" data-testid={`creator-expand-${c.assignment_id}`}>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {Object.entries(c.signals || {}).map(([k, v]) => {
                            const WEIGHTS = { similarity: 30, audience_overlap: 25, historical_performance: 20, engagement_velocity: 15, content_style: 10 };
                            const weight = WEIGHTS[k];
                            return (
                              <div key={k} className="rounded-lg p-2 bg-[#0A0A0A] border border-[#1f1f1f]" data-testid={`signal-${k}`}>
                                <div className="flex items-center justify-between">
                                  <p className="text-[9px] uppercase tracking-wider text-gray-600">{k.replace(/_/g, ' ')}</p>
                                  {weight && (
                                    <span className="text-[8px] font-black tabular-nums text-[#FF4D94]" title={`Weight in composite: ${weight}%`}>
                                      {weight}%
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs font-bold text-white mt-0.5">{v}%</p>
                                <div className="mt-1 h-[3px] rounded-full bg-[#1f1f1f]"><div className="h-full rounded-full bg-[#00D4FF]" style={{ width: `${v}%` }} /></div>
                              </div>
                            );
                          })}
                        </div>
                        {band === 'low' && (
                          <div className="mt-3 flex items-center gap-2 rounded-lg p-2.5 bg-[#FF6B6B10] border border-[#FF6B6B30]">
                            <AlertTriangle className="h-3.5 w-3.5 text-[#FF6B6B] flex-shrink-0" />
                            <p className="text-[11px] text-[#FF6B6B]">Low KPI-hit probability. Try <button onClick={e => { e.stopPropagation(); onAction('story-extension'); }} className="underline font-bold">Story Extension</button> or <button onClick={e => { e.stopPropagation(); onAction('pin-comment-cta'); }} className="underline font-bold">Pin CTA Comment</button>.</p>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!creators.length && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[11px] text-gray-500">No creators assigned yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ═════════════ FlightDeck (right rail) lives in a dedicated component ═════════════
// See /app/frontend/src/components/campaigns/FlightDeck.jsx for the new
// single-line scannable design with 6px colored dots, inline expand, and
// the redesigned RECOMMENDED NOW cards. Removed from this file Feb 2026.

// ═════════════ OptimizationLog (end-of-campaign wrap-up feed) ═════════════
const ACTION_META = {
  hero_mirror:     { icon: Repeat2,        color: '#00D4FF' },
  reply_storm:     { icon: MessageCircle,  color: '#00D4FF' },
  spawn_burst:     { icon: Plus,           color: '#00D4FF' },
  story_extension: { icon: Camera,         color: '#00D4FF' },
  pivot_angle:     { icon: Shuffle,        color: '#FF4D94' },
  whitelist_spark: { icon: Flag,           color: '#00D4FF' },
  pin_comment_cta: { icon: Pin,            color: '#FF4D94' },
  shift_time:      { icon: Clock3,         color: '#FF4D94' },
  creator_swap:    { icon: ArrowRightLeft, color: '#FF4D94' },
  pause_wave:      { icon: Pause,          color: '#FF6B6B' },
};

function OptimizationLog({ actions = [], waveId }) {
  const [open, setOpen] = useState(true);
  const totalCost = actions.reduce((s, a) => s + (Number(a.preview_cost_usd) || 0), 0);
  const totalNotified = actions.reduce((s, a) => s + (a.notified_count || 0), 0);

  return (
    <section className="rounded-2xl bg-[#0F0F0F] border border-[#1f1f1f] overflow-hidden" data-testid="optimization-log">
      <header className="px-5 py-4 border-b border-[#1f1f1f] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList className="h-4 w-4 text-[#FF4D94] flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-black text-white">Optimization Log</p>
            <p className="text-[10px] text-gray-500">
              {actions.length} action{actions.length === 1 ? '' : 's'}
              {totalCost > 0 && <> · ${totalCost.toLocaleString()} extra spend</>}
              {totalNotified > 0 && <> · {totalNotified} creator notifications</>}
              <> · auto-included in end-of-campaign report</>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {waveId && (
            <Link
              to={`/waves/${waveId}/wrap-up`}
              data-testid="open-wrap-up"
              className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#00D4FF] hover:text-white transition-colors"
            >
              Wrap-up report <ChevronRight className="h-3 w-3" />
            </Link>
          )}
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            data-testid="optimization-log-toggle"
            className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-gray-500 hover:text-white transition-colors"
          >
            {open ? <>Hide <ChevronUp className="h-3 w-3" /></> : <>Show <ChevronDown className="h-3 w-3" /></>}
          </button>
        </div>
      </header>
      {open && (
        <div className="max-h-[380px] overflow-y-auto">
          {actions.length === 0 ? (
            <div className="px-5 py-8 text-center text-[11px] text-gray-500">
              No mid-flight optimizations yet. Fire a lever from the Flight Deck to log your first entry.
            </div>
          ) : (
            <ol className="divide-y divide-[#1f1f1f]">
              {actions.map(a => {
                const meta = ACTION_META[a.action_type] || { icon: Sparkles, color: '#00D4FF' };
                const Ic = meta.icon;
                return (
                  <li key={a.action_id} className="px-5 py-3 flex items-start gap-3 hover:bg-[#0A0A0A] transition-colors" data-testid={`log-row-${a.action_id}`}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}30` }}>
                      <Ic className="h-3.5 w-3.5" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2 flex-wrap">
                        <p className="text-[12px] font-bold text-white">{a.label || a.action_type.replace(/_/g, ' ')}</p>
                        <p className="text-[10px] text-gray-500 tabular-nums">{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</p>
                      </div>
                      <p className="text-[11px] text-gray-400 leading-snug mt-0.5">{a.notes || '—'}</p>
                      <div className="flex items-center gap-3 flex-wrap mt-1.5 text-[10px]">
                        {a.preview_cost_usd > 0 && <span className="text-[#FFB84D] font-semibold">+${Number(a.preview_cost_usd).toLocaleString()}</span>}
                        {a.notified_count > 0 && <span className="text-[#00D4FF] font-semibold">{a.notified_count} creators notified</span>}
                        {a.affected_count > 0 && <span className="text-gray-500">{a.affected_count} assignments affected</span>}
                        {a.side_effects?.new_angle && <span className="text-[#FF4D94]">new angle: {a.side_effects.new_angle}</span>}
                        {a.side_effects?.new_post_time && <span className="text-[#FF4D94]">new time: {new Date(a.side_effects.new_post_time).toLocaleString()}</span>}
                        {a.side_effects?.cta_text && <span className="text-gray-400 italic">"{a.side_effects.cta_text}"</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

// ═════════════ client-side suggestion engine ═════════════
function buildSuggestions(liveState, creators, cmdData) {
  // Null-safe — the Control Room renders this page chrome BEFORE the
  // /live-state response arrives (skeleton-first render). Treat missing
  // liveState as "no fast-path suggestions yet" and return whatever CMD
  // recommendations are already cached.
  if (!liveState) liveState = {};
  const out = [];
  const seenLevers = new Set();

  // ── (1) Wave-level CMD reinforcement recommendations (live waves only)
  //     These are the canonical "do this NOW to lift the live wave's
  //     coupling strength" suggestions.
  const cmdRecs = cmdData?.reinforcement_recommendations || [];
  cmdRecs.forEach(r => {
    if (seenLevers.has(r.lever_id)) return;
    seenLevers.add(r.lever_id);
    out.push({
      action_id: r.lever_id,
      path:      r.lever_id.replace(/_/g, '-'),
      title:     `Patch ${r.patch_id} — ${r.patch_role_label}`,
      detail:    r.rationale,
      cmd_flag:  true,
      // Telemetry join key — uniquely identifies this recommendation across
      // (wave_id, patch_id, lever_id). Stamped on wave_actions when this
      // suggestion is fired so calibration can attribute outcomes back to
      // the originating CMD recommendation. See backend `_log_action`.
      cmd_recommendation_id: `${r.patch_id || 'wave'}::${r.lever_id}`,
    });
  });

  // ── (2) Composite-level CMD insights — one suggestion per lagging
  //     composite. This is how the right-rail Flight Deck stays the
  //     single action surface even for waves where only the per-metric
  //     composites have a recommendation (e.g., past waves doing
  //     retrospective analysis, projected waves with pre-launch risks).
  const insights = cmdData?.composite_insights || {};
  ['reach', 'engagement', 'velocity', 'approval', 'diversity'].forEach(metric => {
    const ins = insights[metric];
    if (!ins?.recommended_lever_id) return;
    if (seenLevers.has(ins.recommended_lever_id)) return;
    seenLevers.add(ins.recommended_lever_id);
    const metricLabel = metric.charAt(0).toUpperCase() + metric.slice(1);
    out.push({
      action_id: ins.recommended_lever_id,
      path:      ins.recommended_lever_id.replace(/_/g, '-'),
      title:     `${metricLabel} · Patch ${ins.lagging_patch || '?'} lagging`,
      detail:    ins.analyst_sentence,
      cmd_flag:  true,
      // Telemetry join key — composite-level recommendations get a
      // distinct namespace (metric + lagging_patch + lever) so calibration
      // can separate them from patch-level reinforcements.
      cmd_recommendation_id: `composite::${metric}::${ins.lagging_patch || '?'}::${ins.recommended_lever_id}`,
    });
  });

  // ── (3) Heuristic suggestions (existing) — only fired when CMD didn't
  //     already supply a recommendation for the same lever, so we don't
  //     surface the same lever twice.
  const t = liveState.totals || {};
  const firedPosts = t.fired_posts || 0;
  const top = (creators || []).find(c => c.status === 'live');
  const low = (creators || []).filter(c => c.kpi_hit_probability?.band === 'low').length;

  if (top && (top.kpi_hit_probability?.overall || 0) >= 70 && !seenLevers.has('hero_mirror')) {
    seenLevers.add('hero_mirror');
    out.push({
      action_id: 'hero_mirror', path: 'hero-mirror',
      title: `${top.creator_handle} is over benchmark`,
      detail: `Hero Mirror now → est. +2.1× reach amplification.`,
    });
  }
  if (low >= 2 && !seenLevers.has('reply_storm')) {
    seenLevers.add('reply_storm');
    out.push({
      action_id: 'reply_storm', path: 'reply-storm',
      title: `${low} creators trending low`,
      detail: `Activate Reply Storm to re-ignite algorithm signal.`,
    });
  }
  if (firedPosts >= 3 && firedPosts < (t.total_posts || 0) && !seenLevers.has('spawn_burst')) {
    seenLevers.add('spawn_burst');
    out.push({
      action_id: 'spawn_burst', path: 'spawn-burst',
      title: 'Momentum is live — extend it',
      detail: `Spawn Burst ${(liveState.bursts?.length || 0) + 1} with 3–5 new creators.`,
    });
  }
  // Cap at 5 because the rail is the ONLY action surface (cards/patches
  // are now diagnostic-only). 5 keeps the rail scannable while letting
  // multi-composite recommendations all surface.
  return out.slice(0, 5);
}
